import * as React from 'react';
import { useLocation, useRoute } from 'wouter';
import { useIntl } from 'react-intl';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { CmdLoader } from '@commander-services/gui-components';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import ParkingTagForm from './ParkingTagForm';
import {
  TABLE_CODEBOOKS_PARKING,
  TABLE_CODEBOOKS_RIDE,
  TABLE_COST_CENTERS,
} from '../../Services/TableService';
import { URL_CODEBOOKS } from '../../router/constants';
import PermissionsService, { PERMISSION_PARKING_TAG } from '../../Services/PermissionsService';
import TabComponent from '../TabComponent';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import OnlineHelp from '../OnlineHelp';
import { CmdTable } from '../CmdTable/CmdTable';
import {
  TAG_ACTIVE_KEY,
  TAG_COLOR_KEY,
  TAG_DELETED_AT_KEY,
  TAG_DETAIL_KEY,
  TAG_NAME_KEY,
  PARKING_TAG_TABLE_KEY,
  PARKING_TAG_TABLE_SETTINGS,
  ParkingTagSettingsKeys,
} from './constants';
import { arrayOrder, getForm, getTableSettings } from '../CmdTable/CmdTableService';
import useUrlQueryParams from '../../hooks/useUrlQueryParams';
import { ICmdTableColumn, ICmdTableFilter } from '../CmdTable/types';
import FilterChips from '../CmdTable/FilterChips/FilterChips';
import CmdTableSettings from '../CmdTable/settings/CmdTableSettings';
import { CUSTOMERS_KEY, REQUIRED } from '../CmdTable/constants';
import { TTagRecords, TTagResponse } from './types';
import useParkingTag from './useParkingTag';
import { customersAtom, selectedCustomersAtom } from '../../store/recoil/customers';
import {
  columnsAtom,
  filterForRequestAtomFamily,
  sortFilterAtomFamily,
  tableFiltersAtomFamily,
} from '../CmdTable/CmdTableState';
import { tabsByPermission } from './CodeBooksService';
import PageHeader from '../PageHeader';

export default function ParkingCodebooks() {
  const selectedCustomers = useRecoilValue<number[]>(selectedCustomersAtom);
  const { formatMessage: f } = useIntl();
  const [isModalOpened, setIsModalOpened] = React.useState<boolean>(false);
  const [data, setData] = React.useState<any>([]);
  const [id, setId] = React.useState<number | null>(null);
  const [customerId, setCustomerId] = React.useState<number | null>(null);
  const tabs = React.useState(
    tabsByPermission(
      [TABLE_CODEBOOKS_RIDE, TABLE_CODEBOOKS_PARKING, TABLE_COST_CENTERS],
      TABLE_CODEBOOKS_PARKING,
      selectedCustomers
    )
  )[0];
  const [pathname, navigate] = useLocation();
  const [, params] = useRoute(`${URL_CODEBOOKS}/parking-tag/edit/:id`);
  const { setDocumentTitle } = useDocumentTitle();
  const tableFiltersKey = useRecoilValue(tableFiltersAtomFamily(PARKING_TAG_TABLE_KEY));
  const query = useUrlQueryParams();
  const customerIdUrl = query.get('customerId');
  const filterForRequest = useRecoilValue<ICmdTableFilter>(
    filterForRequestAtomFamily(PARKING_TAG_TABLE_KEY)
  );
  const sortFilter = useRecoilValue<{ [key: string]: string }>(
    sortFilterAtomFamily(PARKING_TAG_TABLE_KEY)
  );
  const queryClient = useQueryClient();
  const setColumnsTable = useSetRecoilState<ICmdTableColumn[]>(columnsAtom);
  const customer = useRecoilValue(customersAtom);
  const { columns, columnOrder, columnVisibility } = useParkingTag();

  const handleClose = React.useCallback(() => {
    navigate(`${URL_CODEBOOKS}/${TABLE_CODEBOOKS_PARKING}`);
    setIsModalOpened(false);
    setData([]);
  }, [navigate]);

  const updateTable = () => {
    queryClient.invalidateQueries({
      queryKey: [PARKING_TAG_TABLE_KEY, filterForRequest, sortFilter, selectedCustomers],
    });
  };

  React.useEffect(() => {
    if (pathname === `${URL_CODEBOOKS}/parking-tag`) {
      const documentTitle = `${f({ id: 'tab.parking-tag' })} - ${f({
        id: 'table.systemSettings',
      })} - ${f({ id: 'menu.settings' })}`;
      setDocumentTitle(documentTitle);
    }
    if (pathname === `${URL_CODEBOOKS}/parking-tag/add`) {
      setIsModalOpened(true);
      setCustomerId(null);
      setId(null);
      setData(data);
      const documentTitle = `${f({ id: 'table.add' })} - ${f({ id: 'tab.parking-tag' })} - ${f({
        id: 'table.systemSettings',
      })} - ${f({ id: 'menu.settings' })}`;
      setDocumentTitle(documentTitle);
    } else if (params) {
      setIsModalOpened(true);
      setData(data);
      setId(Number(params.id));
      setCustomerId(Number(customerIdUrl || null));
      const documentTitle = `${f({ id: 'table.detail' })} - ${f({ id: 'tab.parking-tag' })} - ${f({
        id: 'table.systemSettings',
      })} - ${f({ id: 'menu.settings' })}`;
      setDocumentTitle(documentTitle);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleClose, pathname]);

  const handleSetOrderColumns = (
    newOrderColumns: { name: string; hidden: boolean; visibility: string; sort: boolean }[]
  ) => {
    const tempOrder = newOrderColumns.map((item) => item.name);

    const tempTableSettingOrder: ICmdTableColumn[] = [];

    newOrderColumns.map((item) => {
      if (customer.length === 1 && item.name === CUSTOMERS_KEY) {
        tempTableSettingOrder.push({
          key: item.name,
          default: item.visibility === REQUIRED,
          hidden: true,
        });
      } else {
        tempTableSettingOrder.push({
          key: item.name,
          default: item.visibility === REQUIRED,
          hidden: item.hidden,
        });
      }
    });

    tempTableSettingOrder.push({ key: 'actions', hidden: false, default: false });

    const orderColumns = arrayOrder([...tempTableSettingOrder], [...tempOrder], 'key');

    setColumnsTable(orderColumns);
  };

  const getDataForm = async () => {
    const response = await getForm(
      TABLE_CODEBOOKS_PARKING,
      id || undefined,
      customerId || undefined
    );
    if (response) {
      setData(response);

      return response;
    }
    setData([]);
    return [];
  };

  const getSettings = async () => {
    const settings = await getTableSettings(PARKING_TAG_TABLE_KEY);
    if (settings) {
      handleSetOrderColumns(settings.columns);

      return settings;
    }
    return false;
  };

  const results = useQueries({
    queries: [
      {
        queryKey: ['parkingTagForm', id, customerId, isModalOpened],
        queryFn: () => getDataForm(),
        enabled: isModalOpened,
        staleTime: 0,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      },
      {
        queryKey: [`cmdTableSettings-${PARKING_TAG_TABLE_KEY}`],
        queryFn: () => getSettings(),
        staleTime: 0,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      },
    ],
  });

  const { isLoading: isLoadingDataForm } = results[0];
  const { isLoading: isLoadingSettings } = results[1];

  const handleAddButton = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    navigate(`${URL_CODEBOOKS}/${TABLE_CODEBOOKS_PARKING}/add`);
  };

  const isLoading = isLoadingDataForm || isLoadingSettings;

  return (
    <div>
      <PageHeader
        title={`${f({ id: 'menu.settings' })} / ${f({ id: 'table.systemSettings' })}`}
        onlineHelpTopic="codebooks"
      />
      <div className="w-page mt-8">
        <div className="w-page-content">
          <div className="container-fluid">
            <TabComponent headerStyles={{ marginTop: 0 }} tabs={tabs} />
            <section className="basic-box border-top-thin border-top-gray">
              <div className="basic-box__content">
                <div className="row no-gutters mb-2">
                  <div className="col-12 col-md-auto mr-md-2 mb-2">
                    {PermissionsService.hasAccess(
                      PERMISSION_PARKING_TAG,
                      ['create'],
                      selectedCustomers
                    ) && (
                      <a
                        href="#"
                        data-cy="table-record-addBtn"
                        title={f({ id: 'table.addBtn' })}
                        className="e-button e-button--gray"
                        data-id="addAlarm"
                        onClick={handleAddButton}
                      >
                        {f({ id: 'table.addBtn' })}
                      </a>
                    )}
                  </div>
                  <div className="col col-md" />
                  <div className="col-auto col-md-12 mt-2 ml-2 mt-md-0 ml-md-0 col-lg-auto order-5 order-lg-4 mt-lg-2 mr-2" />
                  <div className="col-auto order-4 order-lg-5">
                    <CmdTableSettings
                      tableId={PARKING_TAG_TABLE_KEY}
                      tableName={PARKING_TAG_TABLE_SETTINGS}
                      sort={{ column: TAG_NAME_KEY, direction: 'desc' }}
                      translationKeys={ParkingTagSettingsKeys}
                    />
                  </div>
                </div>
                <div className="d-flex flex-wrap mb-2">
                  {tableFiltersKey &&
                    tableFiltersKey.map((filterId) => (
                      <FilterChips key={filterId} id={filterId} tableName={PARKING_TAG_TABLE_KEY} />
                    ))}
                </div>
                {isLoading && <CmdLoader inContent={true} />}
                <CmdTable<TTagResponse, TTagRecords>
                  name={PARKING_TAG_TABLE_KEY}
                  stickyColumns={[TAG_DETAIL_KEY, TAG_DELETED_AT_KEY]}
                  lockedColumns={[TAG_ACTIVE_KEY, TAG_COLOR_KEY]}
                  columns={columns}
                  columnOrder={columnOrder}
                  columnVisibility={columnVisibility}
                />
              </div>
            </section>
            {isModalOpened && data.length > 0 && !isLoadingDataForm && (
              <ParkingTagForm
                id={id}
                customerId={customerId}
                data={data}
                updateTable={updateTable}
                handleClose={handleClose}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
